import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Role } from '@pgway/shared';
import { useTier } from '../../hooks/useTier';
import { useMdr } from '../../hooks/useMdr';
import { useAuth } from '../../contexts/AuthContext';

interface NavItem {
  path: string;
  labelKey: string;
  icon: string;
}

const adminNavSuper: NavItem[] = [
  { path: '/admin/dashboard', labelKey: 'nav.dashboard', icon: 'H' },
  { path: '/admin/companies', labelKey: 'nav.companies', icon: 'C' },
  { path: '/admin/sellers', labelKey: 'nav.sellers', icon: 'S' },
  { path: '/admin/mdr', labelKey: 'nav.mdr', icon: 'D' },
  { path: '/admin/users', labelKey: 'nav.users', icon: 'U' },
  { path: '/admin/api-config', labelKey: 'nav.apiConfig', icon: 'A' },
  { path: '/admin/sync', labelKey: 'nav.sync', icon: 'Y' },
];

const adminNavScoped: NavItem[] = [
  { path: '/admin/dashboard', labelKey: 'nav.dashboard', icon: 'H' },
  { path: '/admin/sellers', labelKey: 'nav.sellers', icon: 'S' },
];

// Shown to scoped admins only when they have at least one MDR assigned.
const MDR_NAV: NavItem = { path: '/admin/mdr', labelKey: 'nav.mdr', icon: 'D' };

// Master-only MDR nav entries (hidden from sub MDRs).
const MDR_MASTER_ONLY = new Set(['/mdr/sub-mdrs', '/mdr/groups', '/mdr/assignments']);

const navItems: Record<Role, NavItem[]> = {
  admin: adminNavSuper,
  seller: [
    { path: '/seller/stats', labelKey: 'nav.stats', icon: 'T' },
    { path: '/seller/dashboard', labelKey: 'nav.dashboard', icon: 'H' },
    { path: '/seller/sub-sellers', labelKey: 'nav.subSellers', icon: 'U' },
    { path: '/seller/merchants', labelKey: 'nav.merchants', icon: 'M' },
    { path: '/seller/payouts', labelKey: 'nav.payouts', icon: 'P' },
  ],
  'sub-seller': [
    { path: '/sub-seller/stats', labelKey: 'nav.stats', icon: 'T' },
    { path: '/sub-seller/dashboard', labelKey: 'nav.dashboard', icon: 'H' },
    { path: '/sub-seller/merchants', labelKey: 'nav.merchants', icon: 'M' },
    { path: '/sub-seller/payouts', labelKey: 'nav.payouts', icon: 'P' },
  ],
  mdr: [
    { path: '/mdr/dashboard', labelKey: 'nav.dashboard', icon: 'H' },
    { path: '/mdr/merchants', labelKey: 'nav.merchants', icon: 'M' },
    { path: '/mdr/sub-mdrs', labelKey: 'nav.subMdrs', icon: 'U' },
    { path: '/mdr/groups', labelKey: 'nav.groups', icon: 'G' },
    { path: '/mdr/assignments', labelKey: 'nav.assignments', icon: 'F' },
    { path: '/mdr/payouts', labelKey: 'nav.payouts', icon: 'P' },
  ],
};

interface SidebarProps {
  role: Role;
  /** Mobile off-canvas drawer open state (ignored on lg+ where it's static). */
  mobileOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ role, mobileOpen = false, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { isSuper } = useTier();
  const { isMaster } = useMdr();
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  let items =
    role === 'admin' ? (isSuper ? adminNavSuper : adminNavScoped) : navItems[role] || [];
  // Scoped admins see the MDR menu only when at least one MDR is assigned.
  if (role === 'admin' && !isSuper && (user?.assignedMasterMdrIds?.length ?? 0) > 0) {
    const idx = items.findIndex((i) => i.path === '/admin/sellers');
    items = [...items];
    items.splice(idx >= 0 ? idx + 1 : items.length, 0, MDR_NAV);
  }
  if (role === 'mdr' && !isMaster) {
    items = items.filter((item) => !MDR_MASTER_ONLY.has(item.path));
  }

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 m-4 flex flex-col rounded-2xl bg-white shadow-card transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 dark:bg-gray-800 dark:shadow-card-dark ${
          mobileOpen ? 'translate-x-0' : '-translate-x-[150%] lg:translate-x-0'
        } w-60 ${collapsed ? 'lg:w-20' : 'lg:w-60'}`}
      >
        {/* Brand */}
        <div
          className={`flex h-16 items-center justify-between border-b border-gray-100 px-5 dark:border-gray-700/60 ${
            collapsed ? 'lg:justify-center lg:px-0' : ''
          }`}
        >
          <div className={`flex items-center gap-2 ${collapsed ? 'lg:hidden' : ''}`}>
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-primary text-sm font-bold text-white">
              P
            </span>
            <span className="text-sm font-bold tracking-tight text-gray-800 dark:text-gray-100">
              Seller Manager
            </span>
          </div>
          {/* Desktop collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 lg:flex dark:hover:bg-gray-700"
            aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          >
            {collapsed ? '»' : '«'}
          </button>
          {/* Mobile close */}
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 lg:hidden dark:hover:bg-gray-700"
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>

        {/* Nav */}
        <nav className="scrollbar-thin flex-1 space-y-1 overflow-y-auto px-3 py-4">
          <p
            className={`px-3 pb-1 pt-2 text-[0.65rem] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 ${
              collapsed ? 'lg:hidden' : ''
            }`}
          >
            {t('nav.menu')}
          </p>
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={({ isActive }) =>
                `group flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-gray-50 text-gray-900 shadow-sm dark:bg-gray-700/50 dark:text-white'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700/40 dark:hover:text-gray-100'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-xs font-bold transition-colors ${
                      isActive
                        ? 'bg-gradient-primary text-white shadow-raised'
                        : 'bg-gray-100 text-gray-500 group-hover:text-primary dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {item.icon}
                  </span>
                  <span className={`ml-3 truncate ${collapsed ? 'lg:hidden' : ''}`}>
                    {t(item.labelKey)}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer role chip */}
        <div
          className={`m-3 rounded-xl bg-gradient-primary p-4 text-white ${
            collapsed ? 'lg:hidden' : ''
          }`}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-white/70">
            {t('nav.role')}
          </p>
          <p className="mt-0.5 text-sm font-bold">{t(`roles.${role}`)}</p>
        </div>
      </aside>
    </>
  );
}
